import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);

  // Get stored refresh token from sessionStorage (expires on browser close)
  const getRefreshToken = () => sessionStorage.getItem('refreshToken');
  const setRefreshToken = (token) => {
    if (token) {
      sessionStorage.setItem('refreshToken', token);
    } else {
      sessionStorage.removeItem('refreshToken');
    }
  };

  // Refresh access token using refresh token
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      });

      if (!response.ok) {
        // Refresh token is invalid, clear everything
        setRefreshToken(null);
        setAccessToken(null);
        setUser(null);
        return null;
      }

      const data = await response.json();
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setUser(data.user);
      return data.access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      setRefreshToken(null);
      setAccessToken(null);
      setUser(null);
      return null;
    }
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        await refreshAccessToken();
      }
      setLoading(false);
    };
    initAuth();
  }, [refreshAccessToken]);

  // Set up token refresh interval
  useEffect(() => {
    if (!accessToken) return;

    // Refresh token 5 minutes before expiry (tokens expire in 60 mins)
    const refreshInterval = setInterval(() => {
      refreshAccessToken();
    }, 55 * 60 * 1000); // 55 minutes

    return () => clearInterval(refreshInterval);
  }, [accessToken, refreshAccessToken]);

  // Login function
  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    setAccessToken(data.access_token);
    setRefreshToken(data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  // Logout function
  const logout = async () => {
    const refreshToken = getRefreshToken();
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  // Get authorization header for API calls
  const getAuthHeader = useCallback(() => {
    if (!accessToken) return {};
    return { 'Authorization': `Bearer ${accessToken}` };
  }, [accessToken]);

  // Authenticated fetch wrapper
  const authFetch = useCallback(async (url, options = {}) => {
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${accessToken}`
      }
    });

    // If token expired, try to refresh and retry
    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        return fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${newToken}`
          }
        });
      }
    }

    return response;
  }, [accessToken, refreshAccessToken]);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    login,
    logout,
    getAuthHeader,
    authFetch,
    accessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
